import type {
	ImageUmbBlockElementModel,
	ImageUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import {
	ImageBlock as ImageBlockComponent,
	type ImageBlockLink,
	type ImageBlockRounded,
} from "#/_packages/umbrella/features/image-block/components/image-block";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import {
	getMediaAlt,
	getResponsiveImageProps,
} from "#/_packages/umbrella/integrations/umbraco/utils/image-utils.ts";
import { getUmbLink } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils.ts";
import {
	tryGetAnimationScrollType,
	tryGetAspectRatio,
	tryGetBoxVariant,
} from "#/_packages/umbrella/utils";
import { useCmsImageOptions } from "../../utils/media-composition";

type ImageBlockProps = BlockItemProps & {
	content: ImageUmbBlockElementModel;
	settings: ImageUmbBlockSettingsElementModel;
};

export default function ImageUmbBlock({
	className,
	content,
	settings,
	mediaPriority,
	mediaSizes,
}: ImageBlockProps) {
	const image = content.properties?.image?.[0];
	const settingsProperties = settings.properties;
	const aspectRatio = tryGetAspectRatio(
		content.properties?.aspectRatio,
		"none",
	);
	const mediaOptions = useCmsImageOptions(
		content.id,
		"full",
		mediaPriority,
		mediaSizes,
	);
	if (!image?.url) return null;

	const rawLink = content.properties?.link?.[0];
	const link: ImageBlockLink | undefined = getUmbLink(rawLink);

	return (
		<ImageBlockComponent
			id={settingsProperties?.generatedId ?? undefined}
			className={className}
			{...getResponsiveImageProps(image, { aspectRatio, ...mediaOptions })}
			alt={getMediaAlt(image)}
			caption={content.properties?.imageCaption ?? undefined}
			aspectRatio={tryGetAspectRatio(content.properties?.aspectRatio)}
			rounded={(settingsProperties?.rounded as ImageBlockRounded) ?? undefined}
			boxVariant={tryGetBoxVariant(settingsProperties?.boxVariant)}
			animationScrollType={tryGetAnimationScrollType(
				settingsProperties?.animationScrollType,
			)}
			hoverItemClasses={toHoverItemCssClasses(
				settingsProperties?.animationHoverType,
			)}
			hoverMediaClasses={toHoverMediaCssClasses(
				settingsProperties?.animationHoverType,
			)}
			link={link}
		/>
	);
}
